import { service } from './base-service'
import * as storageService from './storage-service'

const getPersons = () => {
  return service.get('/person')
}

const getPersonById = (personId) => {
  if (!personId) {
    throw new Error('Person Id is required!')
  }
  return service.get(`/person/${personId}`)
}

const isValidDate = (personId, date) => {
  if (!personId) {
    throw new Error('Person Id is required!')
  }
  if (!date) {
    throw new Error('Date Id is required!')
  }
  return service.get(`/person/isValidDate/${personId}?date=${date}`)
}

const getPersonByDocument = (document) => {
  if (!document) {
    throw new Error('Client name is required!')
  }
  return service.get(`/person/document/${document}`)
}

const savePerson = (personData) => {
  if (personData._id) {
    return update(personData._id, personData)
  }
  delete personData._id
  return create(personData)
}

const create = (personData) => {
  delete personData._id
  return service.post(`/person`, personData)
}

const update = (personId, personData) => {
  return service.put(`/person/${personId}`, personData)
}

const updatePersonContacts = (personId, contactData) => {
  return service.patch(`/person/${personId}/contacts`, contactData)
}

const uploadPhoto = (personID, file) => {
  const formData = new FormData()
  formData.append('file', file)
  return storageService.upload(
    formData,
    `person/${personID}/upload`
  )
}

const parseCustomData = (customFields, schema) => {
  const data = {}

  const { fields } = schema || []
  for (const field of fields) {
    let value = customFields[field.model]
    if (value === '') {
      value = null
    }

    if (field.type !== 'textArea' && field.inputType !== 'text') {
      if (typeof value === 'string' && value.replace(/\s*/g, '') === '-') {
        value = null
      }
    }

    switch (field.type) {

      case 'telefone':
        if (typeof value === 'string') {
          value = value.replace(/[^\d]+/g, '')
        }
        break

      case 'textArea':
        if (value == null) {
          value = '-'
        }
        break
    }

    switch (field.inputType) {
      case 'text':
        if (value == null) {
          value = '-'
        }
        break
    }

    data[field.model] = value
  }

  return data
}

const checkHasDuplicatedContacts = async (email, phone, client, excludePersonId = null) => {
  const params = new URLSearchParams()
  if (email) params.append('email', email)
  if (phone) params.append('phone', phone)
  params.append('client', client)
  if (excludePersonId) params.append('excludePersonId', excludePersonId)
  const response = await service.get(`/person/has-duplicated-contacts?${params.toString()}`)
  return response.data // { hasDuplicate: boolean, duplicatedField: string|null }
}

const validateContactUniqueness = async (email, phone, client, personId = null) => {
  if (!client) {
    throw new Error('Client is required!')
  }

  // Same rule as the save: phone equivalence (with or without 55), active bonds only
  const { hasDuplicate, duplicatedField } = await checkHasDuplicatedContacts(
    email,
    phone,
    client,
    personId
  )

  if (!hasDuplicate) {
    return { isValid: true, duplicates: [] }
  }

  return {
    isValid: false,
    duplicates: await getDuplicateOwners(
      duplicatedField,
      email,
      phone,
      client,
      personId
    ),
    duplicatedField
  }
}

const getDuplicateOwners = async (
  duplicatedField,
  email,
  phone,
  client,
  personId
) => {
  const byEmail = duplicatedField !== 'phone'

  try {
    const response = await getDuplicatedContacts(
      byEmail ? email : null,
      byEmail ? null : phone,
      client
    )
    const duplicates = (response.data || []).filter(
      (d) => d.personId !== personId
    )

    return [
      ...duplicates.filter((d) => d.status !== 'Inativo'),
      ...duplicates.filter((d) => d.status === 'Inativo')
    ]
  } catch (error) {
    console.error('[ERROR FETCHING DUPLICATE OWNERS]', error)
    return []
  }
}

const getDuplicatedContacts = (email, phone, client, limit = null, page = null, excludePersonId = null) => {
  if (!client) {
    throw new Error('Client is required!')
  }

  const params = new URLSearchParams()
  if (email) params.append('email', email)
  if (phone) params.append('phone', phone)
  params.append('client', client)
  if (limit) params.append('limit', limit)
  if (page) params.append('page', page)
  if (excludePersonId) params.append('excludePersonId', excludePersonId)

  return service.get(`/person/duplicated-contacts?${params.toString()}`)
}

const exportDuplicatedContacts = async (email, phone, client) => {
  if (!client) {
    throw new Error('Client is required!')
  }

  const params = new URLSearchParams()
  if (email) params.append('email', email)
  if (phone) params.append('phone', phone)
  params.append('client', client)

  return service.get(`/person/duplicated-contacts/export?${params.toString()}`, {
    responseType: 'blob'
  })
}

export {
  getPersons,
  getPersonById,
  getPersonByDocument,
  savePerson,
  create,
  update,
  updatePersonContacts,
  uploadPhoto,
  isValidDate,
  parseCustomData,
  checkHasDuplicatedContacts,
  validateContactUniqueness,
  getDuplicatedContacts,
  exportDuplicatedContacts
}
